import { useEffect, useMemo, useRef, useState } from 'react'
import { Package, ClipboardList } from 'lucide-react'

export interface MentionItem {
  id: string
  label: string
  sublabel?: string
  kind: 'consignment' | 'inquiry'
}

interface Props {
  items: MentionItem[]
  query: string
  onSelect: (item: MentionItem) => void
  onDismiss: () => void
}

/**
 * Record picker for `@` mentions.
 *
 * Mentions exist so the operator can point at a record without pasting a UUID.
 * The inserted text carries the label; the id travels separately, so the model
 * gets an unambiguous reference while the operator reads something meaningful.
 */
export function MentionCombobox({ items, query, onSelect, onDismiss }: Props) {
  const [active, setActive] = useState(0)
  const listRef = useRef<HTMLUListElement>(null)

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase()
    const matches = needle
      ? items.filter(
          (item) =>
            item.label.toLowerCase().includes(needle) ||
            item.sublabel?.toLowerCase().includes(needle),
        )
      : items
    return matches.slice(0, 8)
  }, [items, query])

  useEffect(() => {
    setActive(0)
  }, [query])

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (filtered.length === 0) return
      if (event.key === 'ArrowDown') {
        event.preventDefault()
        setActive((index) => (index + 1) % filtered.length)
      } else if (event.key === 'ArrowUp') {
        event.preventDefault()
        setActive((index) => (index - 1 + filtered.length) % filtered.length)
      } else if (event.key === 'Enter' || event.key === 'Tab') {
        event.preventDefault()
        const item = filtered[active]
        if (item) onSelect(item)
      } else if (event.key === 'Escape') {
        event.preventDefault()
        onDismiss()
      }
    }
    // Capture phase so the composer's own Enter handler does not send the
    // message while the picker is open.
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [filtered, active, onSelect, onDismiss])

  if (filtered.length === 0) {
    return null
  }

  return (
    <ul
      ref={listRef}
      role="listbox"
      aria-label="Mention a record"
      className="absolute bottom-full left-0 z-50 mb-2 max-h-64 w-full overflow-auto rounded-md border border-border bg-popover p-1 shadow-md"
    >
      {filtered.map((item, index) => (
        <li key={item.id}>
          <button
            type="button"
            role="option"
            aria-selected={index === active}
            onMouseDown={(event) => {
              event.preventDefault()
              onSelect(item)
            }}
            onMouseEnter={() => setActive(index)}
            className={`flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm ${
              index === active ? 'bg-accent text-accent-foreground' : ''
            }`}
          >
            {item.kind === 'consignment' ? (
              <Package className="size-3.5 shrink-0 text-muted-foreground" />
            ) : (
              <ClipboardList className="size-3.5 shrink-0 text-muted-foreground" />
            )}
            <span className="truncate">{item.label}</span>
            {item.sublabel && (
              <span className="ml-auto truncate text-xs text-muted-foreground">
                {item.sublabel}
              </span>
            )}
          </button>
        </li>
      ))}
    </ul>
  )
}
